import { useState, useEffect, useRef, useCallback } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { logMyEvent } from '../lib/audit';

// ─────────────────────────────────────────────────────────────────────────────
// usePinLock — manages PIN-based session locking
//
// Security guarantee:
//   • On every page load (refresh, browser reopen, session restore) the app
//     starts as LOCKED when pin_enabled = true.
//   • Only after successful PIN verification does the app unlock.
//   • Fresh logins (user just entered credentials) skip the initial lock.
//   • Inactivity lock fires after pinLockTimeoutMinutes without interaction.
// ─────────────────────────────────────────────────────────────────────────────

export interface PinLockState {
  isLocked: boolean;
  isPinEnabled: boolean;
  timeoutMinutes: number;
  attempts: number;
  /** True once we've determined whether to lock or not (avoids content flash) */
  pinCheckComplete: boolean;
  verifyPin: (pin: string) => Promise<boolean>;
  activatePin: (pin: string) => Promise<void>;
  deactivatePin: () => Promise<void>;
  changePin: (oldPin: string, newPin: string) => Promise<boolean>;
  updateTimeout: (minutes: number) => Promise<void>;
  lock: () => void;
  pinError: string;
}

// ── Crypto helpers ─────────────────────────────────────────────────────────
async function hashPin(pin: string, salt: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(`${pin}:${salt}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── sessionStorage helpers ─────────────────────────────────────────────────
const SESSION_LOCKED_KEY = 'pin_locked';
const FRESH_LOGIN_KEY    = 'pin_fresh_login';

function readSessionLocked(): boolean {
  try { return sessionStorage.getItem(SESSION_LOCKED_KEY) === 'true'; } catch { return false; }
}
function writeSessionLocked(v: boolean): void {
  try {
    if (v) sessionStorage.setItem(SESSION_LOCKED_KEY, 'true');
    else   sessionStorage.removeItem(SESSION_LOCKED_KEY);
  } catch { /* ignore */ }
}

/** Call this once from AuthContext when a fresh credential-based login occurs */
export function markFreshLogin(): void {
  try { sessionStorage.setItem(FRESH_LOGIN_KEY, 'true'); } catch { /* ignore */ }
}

function consumeFreshLogin(): boolean {
  try {
    const v = sessionStorage.getItem(FRESH_LOGIN_KEY) === 'true';
    sessionStorage.removeItem(FRESH_LOGIN_KEY);
    return v;
  } catch { return false; }
}

// ── Hook params ────────────────────────────────────────────────────────────
interface UsePinLockParams {
  user: User | null;
  pinEnabled: boolean;
  pinHash: string | null;
  pinLockTimeoutMinutes: number;
  onProfileUpdate: () => Promise<void>;
  /** True once profile has been fetched from DB (not just auth user) */
  profileLoaded: boolean;
}

export function usePinLock({
  user,
  pinEnabled,
  pinHash,
  pinLockTimeoutMinutes,
  onProfileUpdate,
  profileLoaded,
}: UsePinLockParams): PinLockState {
  // Start as unlocked; initialization effect will set the correct state
  const [isLocked, setIsLockedRaw] = useState<boolean>(false);
  const [pinCheckComplete, setPinCheckComplete] = useState<boolean>(false);
  const [attempts, setAttempts] = useState(0);
  const [pinError, setPinError] = useState('');

  const lastActivityRef   = useRef<number>(Date.now());
  const timeoutRef        = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks whether we've already run the initialization for this page load
  const initDoneRef       = useRef(false);
  const prevUserIdRef     = useRef<string | null>(null);

  // Wrapped setter that also persists to sessionStorage
  const setIsLocked = useCallback((v: boolean) => {
    setIsLockedRaw(v);
    writeSessionLocked(v);
  }, []);

  // ── Reset when user changes (logout/login) ─────────────────────────────
  useEffect(() => {
    if (prevUserIdRef.current !== null && prevUserIdRef.current !== (user?.id ?? null)) {
      // User changed → reset everything
      initDoneRef.current = false;
      setIsLocked(false);
      setPinCheckComplete(false);
      setAttempts(0);
      setPinError('');
    }
    prevUserIdRef.current = user?.id ?? null;
  }, [user?.id, setIsLocked]);

  // ── CORE: Initialization effect ─────────────────────────────────────────
  // Runs once per page load once profile is available.
  // Determines: lock immediately (session restore) vs stay unlocked (fresh login).
  useEffect(() => {
    if (!profileLoaded) return;   // wait for profile
    if (initDoneRef.current) return; // already ran for this page load
    initDoneRef.current = true;

    if (!pinEnabled) {
      // No PIN — nothing to lock, complete immediately
      writeSessionLocked(false);
      setPinCheckComplete(true);
      return;
    }

    // PIN is enabled — decide based on login source
    const isFreshLogin = consumeFreshLogin();

    if (isFreshLogin) {
      // User just logged in with credentials — no PIN required yet
      writeSessionLocked(false);
      setIsLockedRaw(false);
    } else {
      // Session restore (page refresh, browser reopen, tab restore)
      // → ALWAYS lock
      setIsLockedRaw(true);
      writeSessionLocked(true);
    }

    setPinCheckComplete(true);
  }, [profileLoaded, pinEnabled, setIsLocked]);

  // ── Clear lock immediately if PIN is disabled mid-session ──────────────
  useEffect(() => {
    if (!pinEnabled && isLocked) {
      setIsLocked(false);
      initDoneRef.current = false;
    }
  }, [pinEnabled, isLocked, setIsLocked]);

  // ── Activity tracking ──────────────────────────────────────────────────
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!pinEnabled) return;
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll', 'wheel'];
    events.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, resetActivity));
  }, [pinEnabled, resetActivity]);

  // ── Periodic inactivity check (every 30s) ─────────────────────────────
  useEffect(() => {
    if (!pinEnabled || !pinCheckComplete) return;
    const interval = setInterval(() => {
      if (isLocked) return;
      const elapsedMs = Date.now() - lastActivityRef.current;
      const elapsedMinutes = elapsedMs / 1000 / 60;
      if (elapsedMinutes >= pinLockTimeoutMinutes) {
        setIsLocked(true);
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [pinEnabled, pinCheckComplete, isLocked, pinLockTimeoutMinutes, setIsLocked]);

  // ── Tab visibility: lock if inactive when tab returns ─────────────────
  useEffect(() => {
    if (!pinEnabled || !pinCheckComplete) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Check sessionStorage first (tab discard recovery)
        if (readSessionLocked()) {
          setIsLockedRaw(true);
          return;
        }
        if (!isLocked) {
          const elapsedMs = Date.now() - lastActivityRef.current;
          if (elapsedMs / 1000 / 60 >= pinLockTimeoutMinutes) {
            setIsLocked(true);
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [pinEnabled, pinCheckComplete, isLocked, pinLockTimeoutMinutes, setIsLocked]);

  // ── Cleanup ────────────────────────────────────────────────────────────
  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // ── PIN verification ───────────────────────────────────────────────────
  const verifyPin = useCallback(async (pin: string): Promise<boolean> => {
    if (!user || !pinHash) return false;
    const hash = await hashPin(pin, user.id);
    if (hash === pinHash) {
      setIsLocked(false);
      setAttempts(0);
      setPinError('');
      lastActivityRef.current = Date.now();
      return true;
    }
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    if (newAttempts >= 5) {
      setPinError('Muitas tentativas incorretas. Use seu login para continuar.');
    } else {
      setPinError(`PIN incorreto. ${5 - newAttempts} tentativa${5 - newAttempts === 1 ? '' : 's'} restante${5 - newAttempts === 1 ? '' : 's'}.`);
    }
    return false;
  }, [user, pinHash, attempts, setIsLocked]);

  // ── PIN management ─────────────────────────────────────────────────────
  const activatePin = useCallback(async (pin: string) => {
    if (!user) return;
    const hash = await hashPin(pin, user.id);
    await supabase.from('profiles').update({
      pin_enabled: true,
      pin_hash: hash,
      updated_at: new Date().toISOString(),
    }).eq('id', user.id);
    logMyEvent('settings_change', { type: 'pin_activated' });
    await onProfileUpdate();
  }, [user, onProfileUpdate]);

  const deactivatePin = useCallback(async () => {
    if (!user) return;
    await supabase.from('profiles').update({
      pin_enabled: false,
      pin_hash: null,
      updated_at: new Date().toISOString(),
    }).eq('id', user.id);
    setIsLocked(false);
    setAttempts(0);
    setPinError('');
    initDoneRef.current = false;
    logMyEvent('settings_change', { type: 'pin_deactivated' });
    await onProfileUpdate();
  }, [user, onProfileUpdate, setIsLocked]);

  const changePin = useCallback(async (oldPin: string, newPin: string): Promise<boolean> => {
    if (!user || !pinHash) return false;
    const oldHash = await hashPin(oldPin, user.id);
    if (oldHash !== pinHash) return false;
    const newHash = await hashPin(newPin, user.id);
    await supabase.from('profiles').update({
      pin_hash: newHash,
      updated_at: new Date().toISOString(),
    }).eq('id', user.id);
    logMyEvent('settings_change', { type: 'pin_changed' });
    await onProfileUpdate();
    return true;
  }, [user, pinHash, onProfileUpdate]);

  const updateTimeout = useCallback(async (minutes: number) => {
    if (!user) return;
    await supabase.from('profiles').update({
      pin_lock_timeout_minutes: minutes,
      updated_at: new Date().toISOString(),
    }).eq('id', user.id);
    logMyEvent('settings_change', { type: 'pin_timeout_changed', timeout_minutes: minutes });
    await onProfileUpdate();
  }, [user, onProfileUpdate]);

  return {
    isLocked,
    isPinEnabled: pinEnabled,
    timeoutMinutes: pinLockTimeoutMinutes,
    attempts,
    pinCheckComplete,
    verifyPin,
    activatePin,
    deactivatePin,
    changePin,
    updateTimeout,
    lock: () => setIsLocked(true),
    pinError,
  };
}
